import {
  createBrowserRouter,
  RouterProvider as ReactRouterProvider,
} from "react-router-dom";
import PublicRouteWrapper from "./PublicRouteWrapper";
import PrivateRouteWrapper from "./PrivateRouteWrapper";
import routes, { dashboardRoute, getRelativePath } from "./routes";
import EmailConfirmation from "../features/Account/EmailConfirmation";
import ErrorBoundary from "../features/Errors/Errors";
import Home from "../features/Account/Home/Home";
import Inactive from "../features/Account/Inactive";
import Login from "../features/Account/Login";
import Root from "../features/Root";
import PasswordResetForm from "../features/Account/PasswordResetForm";
import PasswordResetKickOff from "../features/Account/PasswordResetKickOff";
import SignUp, { SignUpRedirect } from "../features/Account/SignUp";
import Company from "../features/Company/Company";
import CompanyDetails from "../features/Company/CompanyDetails";
import CompanyReps from "../features/Company/CompanyReps";
import Settings from "../features/Settings/Common/Settings";
import Profile from "../features/Settings/Profile/Profile";
import Password from "../features/Settings/Password/Password";
import { Company as CompanySet } from "../features/Settings/Company/Company";
import Subscription from "../features/Settings/Subscription/Subscription";
import Template from "../features/commons/Template/Template";
import Notifications from "../features/Notifications/Notifications";
import Vault from "../features/Vault/Vault";
import Bank from "../features/Vault/Bank/Bank";
import Finance from "../features/Vault/Finance";
import ID from "../features/Vault/ID";
import Other from "../features/Vault/Other";
import PasswordProtector, {
  PasswordProtectorMirror,
} from "./PasswordProtector";
import Reseller from "../features/Reseller/Reseller";
import Clients from "../features/Reseller/Clients/Clients";
import Assessment from "../features/Financing/Assessment/Assessment";
import ResellerRoute from "../features/Reseller/ResellerRoute";
import Account from "../features/Account/Account";
import Users from "../features/Settings/Users/Users";
import UsersRedirect from "../features/Settings/Users/UsersRedirect";
import Financing from "../features/Financing/Financing";
import Unlock from "../features/Financing/Unlock/Unlock";
import DashboardRoute from "./DashboardRoute";
import Request from "../features/Financing/Request";
import Loans from "../features/Financing/Loans/Loans";
import Redirect from "../features/commons/Template/Navigation/Redirect";
import KeyRedirect from "../features/Account/KeyRedirect";
import Treasury from "../features/Treasury/Treasury";
import Graph from "../features/Treasury/Components/Graph/Graph";
import Debt from "../features/Treasury/Components/Debt";
import Alerts from "../features/Treasury/Components/Alerts";
import Partners from "../features/Treasury/Components/Partners/Partners";
import ThemeSettings from "../features/Settings/Theme/ThemeSettings";
import ThemeRedirect from "../features/Settings/Theme/ThemeRedirect";
import Onboarding, {
  OnboardingRedirect,
} from "../features/Onboarding/Onboarding";
import { OnboardingProvider } from "../features/Onboarding/OnboardingContext";

export default function RouterProvider() {
  const routesObj = {
    element: <Root />,
    path: "/",
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <PublicRouteWrapper>
            <Home />
          </PublicRouteWrapper>
        ),
      },
      {
        path: `${routes.Redirect}/:action`,
        element: <Redirect />,
      },
      {
        path: routes.Onboarding.root,
        element: (
          <PublicRouteWrapper>
            <Template>
              <OnboardingRedirect>
                <OnboardingProvider>
                  <Onboarding />
                </OnboardingProvider>
              </OnboardingRedirect>
            </Template>
          </PublicRouteWrapper>
        ),
      },
      {
        path: routes.Account.root,
        element: <Account />,
        children: [
          {
            path: getRelativePath(routes.Account.Login),
            element: (
              <PublicRouteWrapper>
                <Template>
                  <Login />
                </Template>
              </PublicRouteWrapper>
            ),
          },
          {
            path: getRelativePath(routes.Account.SignUp),
            element: (
              <PublicRouteWrapper>
                <Template>
                  <SignUpRedirect />
                </Template>
              </PublicRouteWrapper>
            ),
          },
          {
            path: routes.Account.SignUpInvite,
            element: (
              <PublicRouteWrapper>
                <Template>
                  <SignUp />
                </Template>
              </PublicRouteWrapper>
            ),
          },
          {
            path: routes.Account.SignUpDirect,
            element: (
              <PublicRouteWrapper>
                <Template>
                  <SignUp />
                </Template>
              </PublicRouteWrapper>
            ),
          },
          {
            path: `${getRelativePath(routes.Account.EmailConfirmation)}/:key`,
            element: (
              <Template>
                <KeyRedirect target={routes.Account.EmailConfirmation} />
              </Template>
            ),
          },
          {
            path: getRelativePath(routes.Account.EmailConfirmation),
            element: (
              <Template>
                <EmailConfirmation />
              </Template>
            ),
          },
          {
            path: getRelativePath(routes.Account.Inactive),
            element: (
              <PublicRouteWrapper>
                <Template>
                  <Inactive />
                </Template>
              </PublicRouteWrapper>
            ),
          },
          {
            path: routes.Account.PasswordReset,
            element: (
              <Template>
                <PasswordResetKickOff />
              </Template>
            ),
          },
          {
            path: `${routes.Account.PasswordReset}/:key`,
            element: (
              <Template>
                <KeyRedirect target={routes.Account.PasswordResetCodeOK} />
              </Template>
            ),
          },
          {
            path: routes.Account.PasswordResetCodeOK,
            element: (
              <Template>
                <PasswordResetForm />
              </Template>
            ),
          },
        ],
      },
      {
        path: routes.Company.root,
        element: (
          <PrivateRouteWrapper>
            <Template>
              <Company />
            </Template>
          </PrivateRouteWrapper>
        ),
        children: [
          {
            path: routes.Company.root,
            element: <CompanyDetails />,
          },
          {
            path: getRelativePath(routes.Company.reps),
            element: <CompanyReps />,
          },
        ],
      },
      {
        path: routes.VaultAllow,
        element: <PasswordProtectorMirror />,
      },
      {
        path: routes.Settings.root,
        element: (
          <PrivateRouteWrapper>
            <Template>
              <Settings />
            </Template>
          </PrivateRouteWrapper>
        ),
        children: [
          {
            path: getRelativePath(routes.Settings.profile),
            element: <Profile />,
          },
          {
            path: getRelativePath(routes.Settings.password),
            element: <Password />,
          },
          {
            path: getRelativePath(routes.Settings.users),
            element: (
              <UsersRedirect>
                <Users />
              </UsersRedirect>
            ),
          },
          {
            path: getRelativePath(routes.Settings.company),
            element: <CompanySet />,
          },
          {
            path: getRelativePath(routes.Settings.subscription),
            element: <Subscription />,
          },
          {
            path: getRelativePath(routes.Settings.theme),
            element: (
              <ThemeRedirect>
                <ThemeSettings />
              </ThemeRedirect>
            ),
          },
        ],
      },
      {
        element: (
          <PrivateRouteWrapper>
            <Template>
              <Notifications />
            </Template>
          </PrivateRouteWrapper>
        ),
        path: routes.Notifications.root,
      },
      {
        element: (
          <PrivateRouteWrapper>
            <PasswordProtector>
              <Template>
                <Vault />
              </Template>
            </PasswordProtector>
          </PrivateRouteWrapper>
        ),
        path: routes.Vault.root,
        children: [
          {
            path: getRelativePath(routes.Vault.bank),
            element: <Bank />,
          },
          {
            path: getRelativePath(routes.Vault.finance),
            element: <Finance />,
          },
          {
            path: getRelativePath(routes.Vault.id),
            element: <ID />,
          },
          {
            path: getRelativePath(routes.Vault.other),
            element: <Other />,
          },
        ],
      },
      {
        element: (
          <PrivateRouteWrapper>
            <ResellerRoute>
              <Template>
                <Reseller />
              </Template>
            </ResellerRoute>
          </PrivateRouteWrapper>
        ),
        path: routes.Reseller.root,
        children: [
          {
            path: getRelativePath(routes.Reseller.clients),
            element: <Clients />,
          },
        ],
      },
      {
        element: (
          <PrivateRouteWrapper>
            <Template>
              <Financing />
            </Template>
          </PrivateRouteWrapper>
        ),
        path: routes.Financing.root,
        children: [
          {
            element: (
              <DashboardRoute>
                <Assessment />
              </DashboardRoute>
            ),
            path: getRelativePath(dashboardRoute),
          },
          {
            element: <Unlock />,
            path: getRelativePath(routes.Financing.unlock),
          },
          {
            element: <Request />,
            path: `${routes.Financing.request}/:requestID`,
          },
          {
            element: <Loans />,
            path: getRelativePath(routes.Financing.loans),
          },
        ],
      },
      {
        element: (
          <PrivateRouteWrapper>
            <Template>
              <Treasury />
            </Template>
          </PrivateRouteWrapper>
        ),
        path: routes.Treasury.root,
        children: [
          {
            path: getRelativePath(routes.Treasury.graph),
            element: <Graph />,
          },
          {
            path: getRelativePath(routes.Treasury.debt),
            element: <Debt />,
          },
          {
            path: getRelativePath(routes.Treasury.alerts),
            element: <Alerts />,
          },
          {
            path: getRelativePath(routes.Treasury.partners),
            element: <Partners />,
          },
        ],
      },
    ],
  };

  const appRouter = createBrowserRouter([routesObj]);

  return <ReactRouterProvider router={appRouter} />;
}
